import React from 'react';
import { DisclosurePanel, StatusCard } from '../components/AppShellPrimitives';

const ONBOARD_FORMAT_OPTIONS = [
  { value: 'smart_trace', label: 'Smart trace analysis (recommended)' },
  { value: 'otel', label: 'OpenTelemetry (OTEL JSON)' },
  { value: 'jsonl', label: 'JSONL Trace Export' },
  { value: 'agent_session_jsonl', label: 'Agent Session JSONL' },
  { value: 'langsmith', label: 'LangSmith JSON' },
  { value: 'langfuse', label: 'Langfuse JSON' },
  { value: 'runtime_json', label: 'Canonical Runtime JSON' },
  { value: 'aws_xray', label: 'AWS X-Ray JSON' },
];
const BASELINE_FORMAT_OPTIONS = ONBOARD_FORMAT_OPTIONS.filter((option) => option.value !== 'agent_session_jsonl');

export default function OnboardingView({
  onboardError,
  onboardSubmitting,
  onboardName,
  setOnboardName,
  onboardSourceType,
  setOnboardSourceType,
  onboardDescription,
  setOnboardDescription,
  setOnboardFile,
  includeBaselineUpload,
  setIncludeBaselineUpload,
  baselineSourceType,
  setBaselineSourceType,
  setBaselineFile,
  onSubmit,
}) {
  const agentSessionSelected = onboardSourceType === 'agent_session_jsonl';
  const selectedOnboardFormat = ONBOARD_FORMAT_OPTIONS.find((option) => option.value === onboardSourceType) || ONBOARD_FORMAT_OPTIONS[0];

  return (
    <section className="content-grid">
      <aside className="panel side-panel">
        <p className="detail-kicker">Onboarding</p>
        <h2>Bring a system online</h2>
        <p className="panel-copy">
          Keep the setup simple: name the system and upload the primary runtime data you want to analyze.
        </p>
        <div className="stack compact-stack">
          <div className="detail-card">
            <strong>Primary upload</strong>
            <p>Required. Use the representative trace set for the workflow you want reviewed first.</p>
          </div>
          <div className="detail-card">
            <strong>Optional baseline</strong>
            <p>Optional. Add older evidence only when before-and-after comparison is part of the review.</p>
          </div>
          <a className="finding-link" href="#/systems">
            Back to systems
          </a>
        </div>
      </aside>
      <div className="panel">
        <div className="panel-header">
          <div>
            <p className="detail-kicker">Create system</p>
            <h2>Upload trace data</h2>
            <p className="panel-copy">The onboarding flow stays intentionally lean while presenting more clearly for enterprise demos.</p>
          </div>
        </div>
        {onboardError ? <StatusCard tone="error">{onboardError}</StatusCard> : null}
        <form className="onboard-form" onSubmit={onSubmit}>
          <div className="form-section">
            <div className="form-section-header">
              <h3>System details</h3>
              <p>Provide a clear name and short description so the demo context reads well on every screen.</p>
            </div>
            <label className="form-field">
              <span>System name</span>
              <input
                className="text-input"
                type="text"
                value={onboardName}
                onChange={(event) => setOnboardName(event.target.value)}
                placeholder="Example: Support Agent"
              />
            </label>
            <label className="form-field">
              <span>System description</span>
              <textarea
                className="text-input"
                rows="4"
                value={onboardDescription}
                onChange={(event) => setOnboardDescription(event.target.value)}
                placeholder="Short summary of this system."
              />
            </label>
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <h3>Trace upload</h3>
              <p>Upload the primary runtime trace you want to inspect. Smart trace analysis is the default for supported JSON and JSONL evidence.</p>
            </div>
            <p className="upload-inline-note">After upload, analysis results appear on the system overview.</p>
            <label className="form-field">
              <span>Trace file (.json or .jsonl)</span>
              <input
                className="file-input"
                type="file"
                accept=".json,.jsonl,application/json,application/jsonl"
                onChange={(event) => setOnboardFile(event.target.files?.[0] || null)}
              />
            </label>
            <DisclosurePanel title="Advanced format override" summaryMeta={selectedOnboardFormat.label}>
              <label className="form-field">
                <span>Format</span>
                <select
                  className="text-input"
                  value={onboardSourceType}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setOnboardSourceType(nextValue);
                    if (nextValue === 'agent_session_jsonl') {
                      setIncludeBaselineUpload(false);
                      setBaselineSourceType('');
                      setBaselineFile(null);
                    }
                  }}
                >
                  {ONBOARD_FORMAT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </DisclosurePanel>
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <h3>Historical baseline</h3>
              <p>Optional guidance data for discussing trend direction and threshold recommendations.</p>
            </div>
            <div className="helper-box helper-box-muted">
              <strong>Optional baseline</strong>
              <p>Skip this unless you have older trace data for comparison. Onboarding works with the primary upload alone.</p>
            </div>
            {agentSessionSelected ? (
              <div className="detail-card">
                <strong>Not available for Agent Session JSONL</strong>
                <p>Historical baseline upload is not supported yet for Agent Session JSONL in this milestone.</p>
              </div>
            ) : (
              <>
                <label className="form-field">
                  <span>Include baseline data</span>
                  <div className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={includeBaselineUpload}
                      onChange={(event) => {
                        const enabled = event.target.checked;
                        setIncludeBaselineUpload(enabled);
                        if (!enabled) {
                          setBaselineFile(null);
                          setBaselineSourceType('');
                        }
                      }}
                    />
                    <span>Add an older trace set to generate advisory baseline guidance.</span>
                  </div>
                </label>
                {includeBaselineUpload ? (
                  <div className="baseline-grid">
                    <label className="form-field">
                      <span>Baseline format</span>
                      <select
                        className="text-input"
                        value={baselineSourceType}
                        onChange={(event) => setBaselineSourceType(event.target.value)}
                      >
                        <option value="">Use primary trace format</option>
                        {BASELINE_FORMAT_OPTIONS.map((option) => (
                          <option key={`baseline-${option.value}`} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="form-field">
                      <span>Baseline trace file (.json or .jsonl)</span>
                      <input
                        className="file-input"
                        type="file"
                        accept=".json,.jsonl,application/json,application/jsonl"
                        onChange={(event) => setBaselineFile(event.target.files?.[0] || null)}
                      />
                    </label>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <div className="form-actions">
            <button className="primary-button" type="submit" disabled={onboardSubmitting}>
              {onboardSubmitting ? 'Uploading…' : 'Start onboarding'}
            </button>
            <a className="secondary-button" href="#/systems">
              Cancel
            </a>
          </div>
        </form>
      </div>
    </section>
  );
}
